import React, { useState, useEffect, useRef } from "react";
import { Badge } from "react-bootstrap";
import { fetchTags } from "../../services/songService";
import { useLanguage } from "../../contexts/LanguageContext";

function TagInput({ tags = [], onChange, ownerType, ownerId }) {
  const { t } = useLanguage();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Load available tags for autocomplete
  useEffect(() => {
    if (ownerType && ownerId) {
      fetchTags(ownerType, ownerId)
        .then((data) => setAllTags(data))
        .catch(() => setAllTags([]));
    }
  }, [ownerType, ownerId]);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const term = inputValue.toLowerCase();
      const currentTagNames = tags.map((t) => t.toLowerCase());
      const filtered = allTags
        .filter(
          (tag) =>
            tag.name.toLowerCase().includes(term) &&
            !currentTagNames.includes(tag.name.toLowerCase())
        )
        .slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [inputValue, allTags, tags]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addTag = (name) => {
    const trimmed = name.trim();
    if (trimmed && !tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      onChange([...tags, trimmed]);
    }
    setInputValue("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeTag = (index) => {
    const next = tags.filter((_, i) => i !== index);
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const tagColors = [
    "bg-primary",
    "bg-success",
    "bg-info",
    "bg-warning text-dark",
    "bg-danger",
    "bg-secondary",
  ];

  const getTagColor = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return tagColors[Math.abs(hash) % tagColors.length];
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div
        className="form-control d-flex flex-wrap align-items-center gap-1"
        style={{ minHeight: "38px", height: "auto", cursor: "text" }}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, index) => (
          <Badge
            key={index}
            className={`${getTagColor(tag)} d-flex align-items-center`}
            style={{ fontSize: "0.85em" }}
          >
            #{tag}
            <span
              role="button"
              className="ms-1"
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={(e) => {
                e.stopPropagation();
                removeTag(index);
              }}
            >
              &times;
            </span>
          </Badge>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim() && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={tags.length === 0 ? t("songbook.form.tagsPlaceholder") : ""}
          style={{
            border: "none",
            outline: "none",
            flex: 1,
            minWidth: "80px",
            background: "transparent",
          }}
        />
      </div>

      {showSuggestions && (
        <div
          className="list-group shadow-sm"
          style={{
            position: "absolute",
            zIndex: 1050,
            width: "100%",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="list-group-item list-group-item-action py-1 px-3"
              onClick={() => addTag(s.name)}
            >
              #{s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default TagInput;
